import { FormControl, SelectChangeEvent } from '@mui/material';
import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGINATION_PAGE_SIZE_STEPS } from 'modules/events/basics/constants/list.constants';
import { EventListSelectStyle } from 'modules/events/components/EventListPagination/EventListPagination.style';
import Box from 'yoda-ui/components/Box';
import Select from 'yoda-ui/components/Form/Select';
import MenuItem from 'yoda-ui/components/MenuItem';
import Pagination from 'yoda-ui/components/Pagination';
import { YodaFontSize } from 'yoda-ui/yodaTheme';

type EventListPaginationProps = {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  setPageNumber: (page: number) => void;
  setPageSize: (size: number) => void;
  onPaginationChange?: () => void;
};

const EventListPagination: FC<EventListPaginationProps> = ({
  pageNumber,
  pageSize,
  totalCount,
  setPageNumber,
  setPageSize,
  onPaginationChange,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalCount / pageSize);
  const rangeStart = pageNumber * pageSize + 1;
  const rangeEnd = Math.min((pageNumber + 1) * pageSize, totalCount);

  const onPageChange = (_event: ChangeEvent<unknown>, value: number) => {
    onPaginationChange?.();
    setPageNumber(value - 1);
  };

  const onPageSizeChange = (event: SelectChangeEvent<unknown>) => {
    onPaginationChange?.();
    setPageSize(event.target.value as number);
    setPageNumber(0);
  };

  if (totalCount <= Math.min(...PAGINATION_PAGE_SIZE_STEPS)) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" marginTop="16px" width="95%" marginX="auto">
      <FormControl>
        <Select
          value={ pageSize }
          onChange={ onPageSizeChange }
          sx={ EventListSelectStyle }
        >
          {
            PAGINATION_PAGE_SIZE_STEPS.map(
              (pageSizeStep: number) => {
                return <MenuItem key={ pageSizeStep }
                  value={ pageSizeStep }>{ pageSizeStep }</MenuItem>;
              },
            )
          }
        </Select>
      </FormControl>
      <Box fontSize={ YodaFontSize.small }>{ t('events_list_pagination_range_label', { rangeStart, rangeEnd, totalCount }) }</Box>
      <Pagination color="primary" count={ totalPages } page={ pageNumber + 1 } size="small" onChange={ onPageChange } />
    </Box>
  );
};

export default EventListPagination;
